/**
 * O campo de busca com criar — o coração do lançamento contínuo.
 *
 * Implementa o contrato publicado: o valor tem **três estados legítimos**, e
 * nenhum deles é erro.
 *
 *   vazio      — ninguém digitou nada
 *   vinculado  — escolhido da lista, tem id
 *   novo       — texto que ainda não existe; a API resolve com get-or-create
 *
 * O terceiro é o que faz o "+" custar zero requisição: o item de pinout vai com
 * `circuitoDescricao` em vez de `circuitoId`, e a RN-03 cria o circuito junto da
 * gravação. Nada é criado antes de gravar.
 *
 * Duas fontes possíveis, e a regra para escolher é mecânica: se o catálogo cabe
 * numa página (200), modo memória e nenhuma tecla vira requisição; se não cabe,
 * modo servidor com 2 caracteres e 400 ms de espera (regra 2).
 */

import { useEffect, useMemo, useRef, useState } from "react"
import type { ItemDeCatalogo } from "@/api/catalogos"

export type ValorBusca =
  | { estado: "vazio" }
  | { estado: "vinculado"; id: number; texto: string }
  | { estado: "novo"; texto: string }

export const VAZIO: ValorBusca = { estado: "vazio" }

export function textoDe(v: ValorBusca): string {
  return v.estado === "vazio" ? "" : v.texto
}

export function idDe(v: ValorBusca): number {
  return v.estado === "vinculado" ? v.id : 0
}

export type FonteBusca = {
  /** "memoria" = lista inteira já em mãos; "servidor" = busca a cada termo. */
  modo: "memoria" | "servidor"
  itens: ItemDeCatalogo[]
  carregando: boolean
  /** Só no modo servidor: avisa o termo digitado. */
  aoBuscar?: (termo: string) => void
  /** O que preencher em outros campos quando este for escolhido. */
  preenche?: (item: ItemDeCatalogo) => void
}

export function CampoDeBusca({
  rotulo, valor, aoMudar, fonte, mono, aoAndar, largura, permiteCriar = true, dica, refCampo, id,
}: {
  rotulo: string
  valor: ValorBusca
  aoMudar: (v: ValorBusca) => void
  fonte: FonteBusca
  mono?: boolean
  /** Enter num valor resolvido anda para o próximo campo — o `EnterProximoCampo`. */
  aoAndar?: () => void
  largura?: number
  permiteCriar?: boolean
  dica?: string
  /** Para a cadeia de foco registrar este campo. */
  refCampo?: (el: HTMLElement | null) => void
  /** Base dos ids das opções — precisa ser único na tela para o `aria-activedescendant`. */
  id?: string
}) {
  const [texto, setTexto] = useState(textoDe(valor))
  const [aberto, setAberto] = useState(false)
  const [marcado, setMarcado] = useState(0)
  const campo = useRef<HTMLInputElement>(null)
  const caixa = useRef<HTMLDivElement>(null)
  const base = id ?? `busca-${rotulo.toLowerCase().replace(/\s+/g, "-")}`
  /**
   * Há texto digitado que ainda não virou valor. Ref, e não estado: quem lê é o
   * `onBlur` que dispara no meio do Enter (o `aoAndar` tira o foco), antes de o
   * valor escolhido chegar de volta — com estado ele leria o texto velho e
   * desfaria a escolha.
   */
  const pendente = useRef(false)
  /**
   * No modo servidor, o texto confirmado ao sair pode chegar antes da resposta da
   * busca (que espera 400 ms): um nome que já existe virava "novo". Guardado aqui,
   * ele vincula sozinho quando a resposta trouxer esse nome.
   */
  const aguardando = useRef<string | null>(null)

  useEffect(() => { setTexto(textoDe(valor)); pendente.current = false }, [valor])

  useEffect(() => {
    const t = aguardando.current
    if (!t || pendente.current) return
    // Só se o valor ainda é o "novo" posto ao sair — a tela pode já ter limpado o campo.
    if (valor.estado !== "novo" || valor.texto !== t) { aguardando.current = null; return }
    const igual = (s?: string) => (s ?? "").toLowerCase() === t.toLowerCase()
    const achado = fonte.itens.find((i) => igual(i.rotulo) || igual(i.codigo))
    if (!achado) return
    aguardando.current = null
    aoMudar({ estado: "vinculado", id: achado.id, texto: achado.codigo || achado.rotulo })
    fonte.preenche?.(achado)
  }, [fonte.itens, valor])

  // Modo servidor: avisa a fonte com espera. Modo memória: nada sai daqui.
  useEffect(() => {
    if (fonte.modo !== "servidor" || !fonte.aoBuscar) return
    const t = texto.trim()
    if (t.length < 2) return
    const id = setTimeout(() => fonte.aoBuscar?.(t), 400)
    return () => clearTimeout(id)
  }, [texto, fonte.modo, fonte.aoBuscar])

  const sugestoes = useMemo(() => {
    const t = texto.trim().toLowerCase()
    const base = fonte.modo === "memoria" && t
      ? fonte.itens.filter(
          (i) => i.rotulo.toLowerCase().includes(t) || (i.codigo ?? "").toLowerCase().includes(t))
      : fonte.itens
    return base.slice(0, 40)
  }, [fonte.itens, fonte.modo, texto])

  // O texto é novo quando não bate exatamente com nenhum item — é o que decide
  // entre `vinculado` e `novo`.
  const exato = sugestoes.find(
    (i) => i.rotulo.toLowerCase() === texto.trim().toLowerCase()
        || (i.codigo ?? "").toLowerCase() === texto.trim().toLowerCase())
  const podeCriar = permiteCriar && texto.trim().length > 0 && !exato

  const total = sugestoes.length + (podeCriar ? 1 : 0)

  function escolher(i: ItemDeCatalogo) {
    pendente.current = false
    aoMudar({ estado: "vinculado", id: i.id, texto: i.codigo || i.rotulo })
    fonte.preenche?.(i)
    setAberto(false)
    aoAndar?.()
  }

  function criar() {
    pendente.current = false
    // Enter antes de a busca responder: o nome pode existir — a resposta decide.
    if (fonte.modo === "servidor") aguardando.current = texto.trim()
    aoMudar({ estado: "novo", texto: texto.trim() })
    setAberto(false)
    aoAndar?.()
  }

  /**
   * O texto digitado vale mesmo sem Enter — saindo do campo com Tab ou com o
   * mouse, ou gravando com Ctrl+Enter daqui de dentro. Antes ele ficava na tela e
   * não ia para o valor: o conector aparecia digitado e o pinout gravava sem ele.
   * O legado mandava sempre o texto do campo.
   *
   * Bate com um item: vincula. Não bate: cria, onde criar é permitido; onde não
   * é, o campo volta a mostrar o que vale, em vez de exibir o que não vai gravar.
   */
  function confirmarDigitado() {
    if (!pendente.current) return
    pendente.current = false
    const t = (campo.current?.value ?? texto).trim()
    if (t === "") { aoMudar(VAZIO); return }
    const igual = (s?: string) => (s ?? "").toLowerCase() === t.toLowerCase()
    const achado = fonte.itens.find((i) => igual(i.rotulo) || igual(i.codigo))
    if (achado) {
      aoMudar({ estado: "vinculado", id: achado.id, texto: achado.codigo || achado.rotulo })
      fonte.preenche?.(achado)
    } else if (permiteCriar) {
      if (fonte.modo === "servidor") aguardando.current = t
      aoMudar({ estado: "novo", texto: t })
    } else setTexto(textoDe(valor))
  }

  function aoTeclar(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setAberto(true); setMarcado((m) => Math.min(m + 1, total - 1)) }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMarcado((m) => Math.max(m - 1, 0)) }
    else if (e.key === "Escape") { setAberto(false) }
    // Ctrl+Enter é o gravar de quem está em volta: aqui só confirma o digitado e
    // deixa a tecla subir, sem andar nem escolher da lista.
    else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { confirmarDigitado(); setAberto(false) }
    else if (e.key === "Enter") {
      e.preventDefault()
      if (aberto && marcado < sugestoes.length) escolher(sugestoes[marcado]!)
      else if (aberto && podeCriar) criar()
      else if (exato) escolher(exato)
      else if (podeCriar) criar()
      else if (texto.trim() === "") { pendente.current = false; aoMudar(VAZIO); aoAndar?.() }
    }
  }

  const tom = valor.estado === "novo" ? "var(--ok)" : valor.estado === "vinculado" ? "var(--line2)" : "var(--line2)"

  return (
    <div ref={(el) => { (caixa as React.MutableRefObject<HTMLDivElement | null>).current = el; refCampo?.(el) }}
         style={{ position: "relative", width: largura ?? "100%" }}>
      <input
        ref={campo}
        value={texto}
        onChange={(e) => { pendente.current = true; aguardando.current = null; setTexto(e.target.value); setAberto(true); setMarcado(0) }}
        onFocus={() => setAberto(true)}
        onBlur={() => { confirmarDigitado(); setTimeout(() => setAberto(false), 120) }}
        onKeyDown={aoTeclar}
        placeholder={dica ?? rotulo}
        aria-label={rotulo}
        role="combobox"
        aria-expanded={aberto}
        aria-controls={`${base}-lista`}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        // O leitor de tela anuncia a opção marcada porque ela é apontada por id;
        // sem isto, as setas mexem visualmente e o leitor não fala nada.
        aria-activedescendant={aberto ? `${base}-op-${marcado}` : undefined}
        className="campo"
        style={{
          width: "100%", padding: "0 9px", borderRadius: 7,
          border: `1px solid ${tom}`, background: "var(--surface)", fontSize: 12.5,
          fontFamily: mono ? "var(--fm)" : undefined,
        }}
      />

      {/* O estado `novo` precisa ser visível: a pessoa tem que saber que vai
          criar, não escolher. */}
      {valor.estado === "novo" && (
        <span
          title="Vai ser criado ao gravar (get-or-create). Nenhuma requisição agora."
          style={{
            position: "absolute", right: 7, top: 7, height: 18, padding: "0 6px",
            borderRadius: 4, background: "var(--ok-soft)", color: "var(--ok-on-soft)",
            fontSize: 9.5, fontWeight: 700, letterSpacing: ".04em", pointerEvents: "none",
          }}
        >
          NOVO
        </span>
      )}

      {aberto && (sugestoes.length > 0 || podeCriar || fonte.carregando) && (
        <div
          id={`${base}-lista`}
          role="listbox"
          aria-label={`Sugestões de ${rotulo}`}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%", width: "max-content",
            maxWidth: 340, maxHeight: 260, overflowY: "auto", zIndex: 70,
            padding: 5, borderRadius: 9, background: "var(--surface)",
            border: "1px solid var(--line2)", boxShadow: "var(--sombra)",
          }}
        >
          {fonte.carregando && <Nota>Buscando…</Nota>}

          {sugestoes.map((i, n) => (
            <button
              key={i.id}
              id={`${base}-op-${n}`}
              type="button"
              role="option"
              aria-selected={n === marcado}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => escolher(i)}
              onMouseEnter={() => setMarcado(n)}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "5px 7px", borderRadius: 6, fontSize: 12.5, textAlign: "left",
                background: n === marcado ? "var(--brand-soft)" : "transparent",
                color: n === marcado ? "var(--brand-on-soft)" : "var(--txt)",
              }}
            >
              {i.codigo && <span style={{ fontFamily: "var(--fm)", fontWeight: 600 }}>{i.codigo}</span>}
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {i.rotulo}
              </span>
              {i.usos > 0 && (
                <span style={{ fontFamily: "var(--fm)", fontSize: 10, color: "var(--txt3)" }}>{i.usos}</span>
              )}
            </button>
          ))}

          {podeCriar && (
            <button
              id={`${base}-op-${sugestoes.length}`}
              type="button"
              role="option"
              aria-selected={marcado === sugestoes.length}
              onMouseDown={(e) => e.preventDefault()}
              onClick={criar}
              onMouseEnter={() => setMarcado(sugestoes.length)}
              style={{
                display: "flex", alignItems: "center", gap: 7, width: "100%",
                marginTop: sugestoes.length > 0 ? 4 : 0,
                paddingTop: sugestoes.length > 0 ? 7 : 5,
                borderTop: sugestoes.length > 0 ? "1px solid var(--line)" : undefined,
                padding: "5px 7px", borderRadius: 6, fontSize: 12.5, textAlign: "left",
                background: marcado === sugestoes.length ? "var(--ok-soft)" : "transparent",
                color: "var(--ok-on-soft)", fontWeight: 600,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                   style={{ flex: "0 0 12px", fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
                <path d="M12 5.5v13M5.5 12h13" />
              </svg>
              Criar <span style={{ fontFamily: mono ? "var(--fm)" : undefined }}>{texto.trim()}</span>
            </button>
          )}

          {fonte.modo === "servidor" && texto.trim().length < 2 && (
            <Nota>Digite 2 caracteres para buscar.</Nota>
          )}
        </div>
      )}
    </div>
  )
}

function Nota({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: "8px 7px", fontSize: 11.5, color: "var(--txt3)" }}>{children}</div>
}
